import type {
  UserSetting,
  UserSettingCSSAppearance,
  UserSettingCSSVariable,
} from "./settings";

export const lightTheme = {
  "--DM__theme-bg-color": "var(--DM__color-light)",
  "--DM__theme-font-color": "var(--DM__color-dark)",
  "--DM__theme-hover-color": "var(--DM__color-grayBase)",
  "--DM__theme-fill-color": "var(--DM__color-light)",
  "--DM__theme-border-color": "var(--DM__color-greenWhite)",
  "--DM__theme-separator-color": "var(--DM__color-grayLight)",
  "--DM__theme-shadow-color": "var(--DM__shadow-grayFonce)",
  "--DM__theme-shadow-text-color": "var(--DM__color-grayShadow)",
  "--DM__theme-error-text-color": "var(--DM__color-errorColor)",
  "--DM__theme-slider-color": "var(--DM__color-sliderColor)",
  "--DM__theme-slider-RGBColor": "var(--DM__color-sliderRGBColor)",
};

export const sepiaTheme = {
  "--DM__theme-bg-color": "var(--DM__color-sepia)",
  "--DM__theme-font-color": "var(--DM__color-darkSepia)",
  "--DM__theme-hover-color": "var(--DM__color-light)",
  "--DM__theme-fill-color": "var(--DM__color-sepia)",
  "--DM__theme-border-color": "var(--DM__color-greenSepia)",
  "--DM__theme-separator-color": "var(--DM__color-grayLight)",
  "--DM__theme-shadow-color": "var(--DM__shadow-grayFonce)",
  "--DM__theme-shadow-text-color": "var(--DM__color-grayShadow)",
  "--DM__theme-error-text-color": "var(--DM__color-errorColor)",
  "--DM__theme-slider-color": "var(--DM__color-darkSepia)",
  "--DM__theme-slider-RGBColor": "var(--DM__color-sliderSepiaRGBColor)",
};

export const nightTheme = {
  "--DM__theme-bg-color": "var(--DM__color-dark)",
  "--DM__theme-font-color": "var(--DM__color-light)",
  "--DM__theme-hover-color": "var(--DM__color-dark)",
  "--DM__theme-fill-color": "var(--DM__color-grayDark)",
  "--DM__theme-border-color": "var(--DM__color-vert)",
  "--DM__theme-separator-color": "var(--DM__color-dark)",
  "--DM__theme-shadow-color": "var(--DM__shadow-night)",
  "--DM__theme-shadow-text-color": "var(--DM__color-grisFonce)",
  "--DM__theme-error-text-color": "var(--DM__color-nightErrorColor)",
  "--DM__theme-slider-color": "var(--DM__color-sliderColor)",
  "--DM__theme-slider-RGBColor": "var(--DM__color-sliderRGBColor)",
};

export const themeMap: Record<
  Exclude<UserSettingCSSAppearance, "default">,
  object
> = {
  day: lightTheme,
  sepia: sepiaTheme,
  night: nightTheme,
};

export const settingName: { [key: string]: UserSettingCSSVariable } = {
  theme: "theme",
  pageLayout: "columnCount",
  overflow: "scroll",
  fontSize: "fontSize",
  fontFamily: "fontFamily",
  fontOverride: "fontOverride",
  pageLayoutPDF: "pageLayoutPDF",
  firstPageByItself: "firstPageByItself",
  customZoom: "customZoomPDF",
  handTool: "handTool",
  ttsVoice: "ttsVoice",
  ttsVoiceVolume: "ttsVoiceVolume",
  ttsHighlight: "ttsHighlight",
  ttsLang: "ttsLang",
  ttsVoiceSpeed: "ttsVoiceSpeed",
  ttsVoicePitch: "ttsVoicePitch",
  advancedSettings: "advancedSettings",
  ttsAutoTurn: "ttsAutoTurn",
};

export const ttsLanguageOptions = [
  {
    label: "Basque",
    value: "eu",
  },
  {
    label: "Catalan",
    value: "ca",
  },
  {
    label: "English",
    value: "en",
  },
  {
    label: "French",
    value: "fr",
  },
  {
    label: "Galician",
    value: "gl",
  },
  {
    label: "Italian",
    value: "it",
  },
  {
    label: "Spanish",
    value: "es",
  },
  {
    label: "Portuguese",
    value: "pt",
  },
];

export const defaultSettings: UserSetting[] = [
  {
    label: "Theme",
    name: settingName.theme,
    default: "default",
    global: true,
    options: [
      {
        label: "Default",
        value: "default",
      },
      {
        label: "Light",
        value: "day",
      },
      {
        label: "Sepia",
        value: "sepia",
      },
      {
        label: "Night",
        value: "night",
      },
    ],
  },
  {
    label: "Font Size",
    name: settingName.fontSize,
    default: "100%",
    value: "100%",
    options: [],
  },
  {
    label: "Font Family",
    name: settingName.fontFamily,
    default: "Default",
    options: [
      {
        label: "Default",
        value: "Default",
      },
      {
        label: "Arial",
        value: "Arial",
      },
      // {
      //   label: "Literata",
      //   value: "Literata",
      // },
      // {
      //   label: "PT Serif",
      //   value: "PTSerif",
      // },
      // {
      //   label: "Roboto Flex",
      //   value: "Roboto",
      // },
      // {
      //   label: "Source Sans Pro",
      //   value: "SourceSansPro",
      // },
      // {
      //   label: "Vollkorn",
      //   value: "Vollkorn",
      // },
      // {
      //   label: "OpenDyslexic",
      //   value: "OpenDyslexic",
      // },
      {
        label: "AccessibleDfA",
        // TODO: how do we add "AccessibleDfA fallback" to the font list?
        value: "AccessibleDfA",
      },
      // {
      //   label: "Atkinson Hyperlegible",
      //   value: "AtkinsonHyperlegible",
      // },
      // {
      //   label: "IA Writer Duospace",
      //   value: "IAWriterDuospace",
      // },
    ],
  },
  {
    label: "Page Layout",
    name: settingName.pageLayout,
    default: 2,
    options: [
      {
        label: "Auto",
        value: 2,
        icon: "hdr_auto",
      },
      {
        label: "Single",
        value: 1,
        icon: "article",
      },
    ],
  },
  {
    label: "Scroll view",
    name: settingName.overflow,
    default: false,
    options: [
      {
        label: "Paginated",
        value: false,
        icon: "swap_horiz",
      },
      {
        label: "Scrolling",
        value: true,
        icon: "swap_vert",
      },
    ],
  },
  {
    label: "Font Override",
    name: settingName.fontOverride,
    default: true,
    options: [
      {
        label: "Reader formatting",
        value: true,
      },
      {
        label: "Original formatting",
        value: false,
      },
    ],
  },
  {
    label: "Advanced settings",
    name: settingName.advancedSettings,
    default: "readium-advanced-on",
    options: [
      {
        label: "Reader formatting",
        value: "readium-advanced-on",
      },
      {
        label: "Original formatting",
        value: "readium-advanced-off",
      },
    ],
  },
  {
    label: "Page Layout PDF",
    name: settingName.pageLayoutPDF,
    default: 1,
    options: [
      {
        label: "Auto",
        value: 1,
        icon: "hdr_auto",
      },
      {
        label: "Single",
        value: 0,
        icon: "article",
      },
    ],
  },
  {
    label: "First page by itself",
    name: settingName.firstPageByItself,
    default: true,
    options: [
      {
        label: "Page By Itself",
        value: true,
        icon: "swap_horiz",
      },
      {
        label: "Page Dual",
        value: false,
        icon: "swap_vert",
      },
    ],
  },
  {
    label: "Cursor Tool",
    name: settingName.handTool,
    default: true,
    options: [
      {
        label: "Select",
        value: false,
        icon: "format_size",
      },
      {
        label: "Hand",
        value: true,
        icon: "pan_tool",
      },
    ],
  },
  {
    label: "Auto Turn Page",
    name: settingName.ttsAutoTurn,
    default: true,
    options: [
      {
        label: "Auto Turn Page",
        value: true,
        icon: "swap_horiz",
      },
      {
        label: "Manual Turn Page",
        value: false,
        icon: "swap_vert",
      },
    ],
  },
  {
    label: "Custom Zoom",
    name: settingName.customZoom,
    default: "0",
    value: "0",
    options: [],
  },
  {
    label: "TTS Highlight",
    name: settingName.ttsHighlight,
    default: "#f0ba32",
    value: "#f0ba32",
    options: [],
  },
  {
    label: "TTS Voice",
    name: settingName.ttsVoice,
    default: "Default",
    value: "Default",
    options: [],
  },
  {
    label: "TTS Language",
    name: settingName.ttsLang,
    default: "en",
    options: ttsLanguageOptions,
  },
  {
    label: "TTS Voice Speed",
    name: settingName.ttsVoiceSpeed,
    default: 1,
    value: 1,
    options: [],
  },
  {
    label: "TTS Voice Pitch",
    name: settingName.ttsVoicePitch,
    default: 1,
    value: 1,
    options: [],
  },
  {
    label: "TTS Voice Volume",
    name: settingName.ttsVoiceVolume,
    default: 50,
    value: 50,
    options: [],
  },
];

export const localStorageInfo = {
  publicationKeys: {
    settings: "settings",
    progression: "progression",
  },
  version: 100,
};

export const zoomOptions = {
  paginated: [
    {
      value: "page-fit",
      translationKey: "page-fit",
    },
    {
      value: "page-width",
      translationKey: "page-width",
    },
    {
      value: "page-height",
      translationKey: "page-height",
    },
    {
      value: "50",
      translationKey: "50",
    },
    {
      value: "100",
      translationKey: "100",
    },
    {
      value: "150",
      translationKey: "150",
    },
    {
      value: "200",
      translationKey: "200",
    },
    {
      value: "300",
      translationKey: "300",
    },
    {
      value: "400",
      translationKey: "400",
    },
  ],
  scrolling: [
    {
      value: "page-fit",
      translationKey: "page-fit",
    },
    {
      value: "page-width",
      translationKey: "page-width",
    },
    {
      value: "page-height",
      translationKey: "page-height",
    },
    {
      value: "50",
      translationKey: "50",
    },
    {
      value: "100",
      translationKey: "100",
    },
    {
      value: "150",
      translationKey: "150",
    },
    {
      value: "200",
      translationKey: "200",
    },
    {
      value: "300",
      translationKey: "300",
    },
    {
      value: "400",
      translationKey: "400",
    },
  ],
  fxl: [
    {
      value: "page-fit",
      translationKey: "page-fit",
    },
    {
      value: "150",
      translationKey: "150",
    },
    {
      value: "200",
      translationKey: "200",
    },
    {
      value: "300",
      translationKey: "300",
    },
    {
      value: "400",
      translationKey: "400",
    },
    {
      value: "500",
      translationKey: "500",
    },
    {
      value: "600",
      translationKey: "600",
    },
  ],
};

export const pdfResource: { name: "application/pdf" } = {
  name: "application/pdf",
};

export const mediaOverlayResource = {
  name: "application/guided-navigation+json",
};

export const customZoom = {
  fit: "page-fit",
  height: "page-height",
  width: "page-width",
};

export const countryLabels = {
  "ca-ES": "España",
  ca_ES: "España",
  "en-AU": "Australia",
  en_AU: "Australia",
  "en-GB": "United Kingdom",
  en_GB: "United Kingdom",
  "en-CA": "Canada",
  en_CA: "Canada",
  "en-HK": "Hong Kong",
  en_HK: "Hong Kong",
  "en-IN": "India",
  en_IN: "India",
  "en-IE": "Ireland",
  en_IE: "Ireland",
  "en-KE": "Kenya",
  en_KE: "Kenya",
  "en-NG": "Nigeria",
  en_NG: "Nigeria",
  "en-NZ": "New Zealand",
  en_NZ: "New Zealand",
  "en-PH": "Philippines",
  en_PH: "Philippines",
  "en-SG": "Singapore",
  en_SG: "Singapore",
  "en-TZ": "Tanzania",
  en_TZ: "Tanzania",
  "en-US": "United States",
  en_US: "United States",
  "en-ZA": "South Africa",
  en_ZA: "South Africa",
  "es-AR": "Argentina",
  es_AR: "Argentina",
  "es-BO": "Bolivia",
  es_BO: "Bolivia",
  "es-CL": "Chile",
  es_CL: "Chile",
  "es-CO": "Colombia",
  es_CO: "Colombia",
  "es-CR": "Costa Rica",
  es_CR: "Costa Rica",
  "es-CU": "Cuba",
  es_CU: "Cuba",
  "es-DO": "Dominican Republic",
  es_DO: "Dominican Republic",
  "es-EC": "Ecuador",
  es_EC: "Ecuador",
  "es-ES": "España",
  es_ES: "España",
  "es-GQ": "Equatorial Guinea",
  es_GQ: "Equatorial Guinea",
  "es-GT": "Guatemala",
  es_GT: "Guatemala",
  "es-HN": "Honduras",
  es_HN: "Honduras",
  "es-NI": "Nicaragua",
  es_NI: "Nicaragua",
  "es-MX": "México",
  es_MX: "México",
  "es-PA": "Panamá",
  es_PA: "Panamá",
  "es-PE": "Perú",
  es_PE: "Perú",
  "es-PR": "Puerto Rico",
  es_PR: "Puerto Rico",
  "es-PY": "Paraguay",
  es_PY: "Paraguay",
  "es-SV": "El Salvador",
  es_SV: "El Salvador",
  "es-US": "Estados Unidos",
  es_US: "Estados Unidos",
  "es-UY": "Uruguay",
  es_UY: "Uruguay",
  "es-VE": "Venezuela",
  es_VE: "Venezuela",
  "eu-ES": "España",
  eu_ES: "España",
  "fr-BE": "Belgique",
  fr_BE: "Belgique",
  "fr-CA": "Canada",
  fr_CA: "Canada",
  "fr-CH": "Suisse",
  fr_CH: "Suisse",
  "fr-FR": "France",
  fr_FR: "France",
  "gl-ES": "España",
  gl_ES: "España",
  "it-IT": "Italia",
  it_IT: "Italia",
  "pt-BR": "Brasil",
  pt_BR: "Brasil",
  "pt-PT": "Portugal",
  pt_PT: "Portugal",
};

export const desktopOS = ["Windows", "Linux", "macOS"];

export const readingDirection = {
  next: "forward",
  prev: "backward",
};

export const ttsPlayerValues = {
  emptyPage: " ",
  chromeTTSPlayTimeout: 5000,
  chromeTTSPauseTimeout: 13000,
};

export const posthogKeys = {
  openPDF: "PDF opened",
  openEPUB: "EPUB opened",
  openFXL: "FXL opened",
  openPDFSample: "PDF sample opened",
  openEPUBSample: "EPUB sample opened",
  openFXLSample: "FXL sample opened",
  readingSettings: "Reading settings opened",
  toc: "TOC opened",
  tts: "TTS opened",
  ttsSettings: "TTS settings opened",
  goToPosition: "Go to position opened",
  sampleShare: "Sample shared",
  sampleDownload: "Sample downloaded",
  sampleCopyLink: "Sample link copied",
  offlineMode: "Offline mode triggered",
  offlineModeFailed: "Offline mode failed",
  offlineModeSuccess: "Offline mode success",
};

export const urlVariables = {
  debug: "showDebug",
};

export const allowedTaggedTags = new Set([
  "P",
  "H",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "LBody",
  "Span",
  "Link",
  "Figure",
  "Table",
]);
export const allowedTaggedType = new Set(["content"]);

/* PDF Container size constants */
export const fallbackAspectRatio = 3 / 4; // Default aspect ratio for PDF pages if not specified
export const minimalScreenWidth = 900; // Minimum screen width for dual page mode
export const minimalScreenHeight = 480; // Minimum screen height for dual page mode

export const highlightColorMap = {
  yellow: "#f0ba32",
  red: "#e8825f",
  green: "#abcb78",
  blue: "#7cbfb8",
} as const satisfies Record<string, `#${string}`>;

export const highlightColorDefinition = {
  light: "#B2EBF2",
  dark: "#FFEB3B",
  sepia: "#E1C8E7",
};
